import { StateService } from "@uirouter/angular";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from "@angular/core";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Observable, finalize, map, of, take, zip } from "rxjs";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { localize } from "@gtmhub/localization";
import { CurrentUserService, getCurrentUserId } from "@gtmhub/users";
import { CommentTargetType, CommentsFacade } from "@webapp/comments";
import { ClipboardService } from "@webapp/core/util/services/clipboard.service";
import { KpiProjection } from "@webapp/kpis/models/kpi-projections.models";
import { Kpi } from "@webapp/kpis/models/kpis.models";
import { DropdownMenuItem } from "@webapp/shared/dropdown/dropdown.models";
import { DropdownMenuItemBuilder } from "@webapp/shared/dropdown/util/dropdown-menu-item-builder";
import dayjs from "@webapp/shared/libs/dayjs";
import { UiModalService } from "@webapp/ui/modal/services/modal.service";
import { CHART_POINT_DATE_FORMAT } from "../../kpi-details/kpi-chart/utils";
import { KpiDetailsStateProxyService } from "../../kpi-details/services/kpi-details-state-proxy.service";
import { shouldShowInfoIcon } from "../utils";

@UntilDestroy()
@Component({
  selector: "kpi-comment",
  templateUrl: "kpi-comment.component.html",
  styleUrls: ["kpi-comment.component.less"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KpiCommentComponent implements OnInit, OnDestroy {
  @Input() public commentId: string;
  @Input() public kpiId: string;

  public kpi: Kpi;
  public kpiDataPointCurrentValue: number;
  public targetDate: string;
  public targetType: CommentTargetType;
  public kpiCommentMenuItems: DropdownMenuItem[];
  public isLoading = true;
  public showInfoIcon = false;
  public metadata: { [key: string]: string } = {};
  private projectionId = null;

  public constructor(
    private commentsFacade: CommentsFacade,
    private cdr: ChangeDetectorRef,
    private kpiDetailsStateProxyService: KpiDetailsStateProxyService,
    private stateService: StateService,
    private clipboardService: ClipboardService,
    private currentUserService: CurrentUserService,
    private modalService: UiModalService,
    private uiErrorHandlingService: UIErrorHandlingService
  ) {}

  public ngOnInit(): void {
    this.getKpi();
    this.getCommentAndItsTargetData();

    this.handleProjectionDeleted();
    this.handleProjectionUpdated();
    this.handleProjectionDragged();
  }

  public ngOnDestroy(): void {
    this.kpiDetailsStateProxyService.setOpenComment(null);
  }

  public onCancel(): void {
    this.kpiDetailsStateProxyService.setOpenComment(null);
  }

  public onCommentRemoved(commentId: string): void {
    if (commentId === this.commentId) {
      this.kpiDetailsStateProxyService.setOpenComment(null);
      this.kpiDetailsStateProxyService.onCommentDeleted(this.commentId);
      this.stateService.go("^");
    }
  }

  private getKpi(): void {
    this.kpiDetailsStateProxyService
      .getKpi$()
      .pipe(untilDestroyed(this))
      .subscribe((kpi) => {
        this.kpi = kpi;
        this.cdr.markForCheck();
      });
  }

  private getCommentAndItsTargetData(): void {
    this.kpiDetailsStateProxyService
      .getComment$(this.commentId)
      .pipe(take(1), untilDestroyed(this))
      .subscribe((comment) => {
        this.targetType = comment.targetType;
        this.targetDate = dayjs(comment.targetDate).format(CHART_POINT_DATE_FORMAT);
        this.kpiDetailsStateProxyService.setOpenComment(comment);
        this.kpiCommentMenuItems = this.getMenuItems(comment.createdBy);
        this.showInfoIcon = shouldShowInfoIcon(this.targetType, this.targetDate);
        this.setMetadata();
        this.findAndProcessDataPoint();
      });
  }

  private setMetadata(): void {
    this.metadata["from"] = "chart";
  }

  private findAndProcessDataPoint(): void {
    if (this.targetType === "kpi_snapshot") {
      this.kpiDetailsStateProxyService
        .getSnapshotByKey$(this.targetDate)
        .pipe(
          take(1),
          untilDestroyed(this),
          finalize(() => (this.isLoading = false))
        )
        .subscribe((snapshot) => {
          this.kpiDataPointCurrentValue = snapshot.value;
          this.cdr.markForCheck();
        });
    } else {
      this.kpiDetailsStateProxyService
        .getProjectionByDate$(this.targetDate)
        .pipe(
          take(1),
          untilDestroyed(this),
          finalize(() => (this.isLoading = false))
        )
        .subscribe((projection) => {
          this.projectionId = projection.id;
          this.kpiDataPointCurrentValue = projection.value;
          this.cdr.markForCheck();
        });
    }
  }

  private handleProjectionDeleted(): void {
    this.kpiDetailsStateProxyService
      .getProjectionDeleted$()
      .pipe(untilDestroyed(this))
      .subscribe((deletedProjectionDate) => {
        const commentBelongToDeletedProjection = this.commentBelongToDeletedProjection(this.targetType, this.targetDate, deletedProjectionDate);

        if (commentBelongToDeletedProjection) {
          this.stateService.go("^");
        }
      });
  }

  private commentBelongToDeletedProjection(targetType: CommentTargetType, targetDate: string, deletedProjectionDate: string): boolean {
    const isProjectionTargetType = targetType === "kpi_projection";
    const formattedTargetDate = dayjs(targetDate).format(CHART_POINT_DATE_FORMAT);
    const formattedDeletedProjectionDate = dayjs(deletedProjectionDate).format(CHART_POINT_DATE_FORMAT);

    return isProjectionTargetType && formattedTargetDate === formattedDeletedProjectionDate;
  }

  private handleProjectionUpdated(): void {
    this.kpiDetailsStateProxyService
      .getProjectionUpdated$()
      .pipe(untilDestroyed(this))
      .subscribe((projection) => this.onProjectionEdited(projection));
  }

  private handleProjectionDragged(): void {
    this.kpiDetailsStateProxyService
      .getProjectionDragged$()
      .pipe(untilDestroyed(this))
      .subscribe((projection) => this.onProjectionEdited(projection));
  }

  private onProjectionEdited(projection: KpiProjection): void {
    const commentBelongsToProjection = this.targetType === "kpi_projection" && this.projectionId === projection.id;

    if (commentBelongsToProjection) {
      this.kpiDataPointCurrentValue = projection.value;
      this.targetDate = projection.date;
      this.cdr.detectChanges();
    }
  }

  private getMenuItems(commentAuthorId: string): DropdownMenuItem[] {
    const menuItemBuilder = new DropdownMenuItemBuilder();

    const copyLinkItem = menuItemBuilder
      .setKey("share_link")
      .setUiType({ uiType: "icon", icon: "link" })
      .setAction({ handler: (): void => this.copyLinkToClipboard() })
      .build();

    const deleteItem = menuItemBuilder
      .setKey("delete_comment")
      .setUiType({ uiType: "uiIcon", iconType: "trash-bin", iconTheme: "outline" })
      .setIsVisibleCondition(this.canDeleteComment$(commentAuthorId))
      .setAction({ handler: (): void => this.openCommentDeleteConfirmation() })
      .setToBeDestructive()
      .build();

    return [copyLinkItem, deleteItem];
  }

  private copyLinkToClipboard(): void {
    this.clipboardService.copyToClipboard(this.getLinkUrl(this.commentId));
  }

  private getLinkUrl(commentId: string): string {
    return this.stateService.href("gtmhub.kpis.details.comment", { commentId }, { absolute: true });
  }

  private canDeleteComment$(commentAuthorId: string): Observable<boolean> {
    const isCurrentUserCommentAuthor$ = of(this.isCurrentUserCommentAuthor(commentAuthorId));
    const isAdmin$ = of(this.isCurrentUserAdmin());

    return zip([isCurrentUserCommentAuthor$, isAdmin$]).pipe(map(([isCurrentUserCommentAuthor, isAdmin]) => isCurrentUserCommentAuthor || isAdmin));
  }

  private isCurrentUserCommentAuthor(commentAuthorId: string): boolean {
    return commentAuthorId === getCurrentUserId();
  }

  private isCurrentUserAdmin(): boolean {
    return this.currentUserService.userHasRole("admin");
  }

  private openCommentDeleteConfirmation(): void {
    const { uiTitle, uiContent } = this.generateConfirmDeleteCopy();
    this.modalService.confirm({
      uiTitle,
      uiContent,
      uiOkText: localize("delete"),
      uiCancelText: localize("cancel"),
      uiOkDanger: true,
      uiIconType: null,
      uiOnOk: () => this.deleteCommentOnConfirm(),
    });
  }

  private deleteCommentOnConfirm(): void {
    this.commentsFacade
      .deleteComment$(this.commentId, { type: "comment" })
      .pipe(take(1), untilDestroyed(this))
      .subscribe({
        next: () => {
          this.onCommentRemoved(this.commentId);
        },
        error: (error) => {
          this.uiErrorHandlingService.handleModal(error);
        },
      });
  }

  private generateConfirmDeleteCopy(): { uiTitle: string; uiContent: string } {
    const defaultTitle = localize("confirm_delete_comment_title");
    const defaultContent = localize("this_cant_be_undone");

    return { uiTitle: defaultTitle, uiContent: `${localize("confirm_delete_comment_and_replies_content")} ${defaultContent}` };
  }
}
